/**
 * Utilitaire pour convertir les images HEIC en JPEG côté client
 * Utilise libheif-js pour décoder les HEIC et canvas pour les convertir en JPEG
 */

import libheif from 'libheif-js/wasm-bundle'

/**
 * Vérifie si un fichier est au format HEIC/HEIF
 * @param {File} file - Le fichier à vérifier
 * @returns {boolean}
 */
export function isHeicFile (file) {
  if (!file) return false
  
  // Vérifier le type MIME
  const heicMimeTypes = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']
  if (file.type && heicMimeTypes.includes(file.type.toLowerCase())) {
    return true
  }
  
  // Vérifier l'extension du fichier si le type MIME n'est pas disponible
  if (file.name) {
    const fileName = file.name.toLowerCase()
    return fileName.endsWith('.heic') || fileName.endsWith('.heif')
  }
  
  return false
}

/**
 * Convertit un fichier HEIC en JPEG et retourne une data URL
 * @param {File} file - Le fichier HEIC à convertir
 * @param {number} quality - Qualité JPEG (0-1), par défaut 0.9
 * @returns {Promise<string>} Data URL du JPEG converti
 */
export async function convertHeicToJpeg (file, quality = 0.9) {
  try {
    // Lire le fichier HEIC en ArrayBuffer
    const buffer = await file.arrayBuffer()
    const uint8Array = new Uint8Array(buffer)

    // Décoder avec libheif
    const decoder = new libheif.HeifDecoder()
    const data = decoder.decode(uint8Array)
    
    if (!data || data.length === 0) {
      throw new Error('Aucune image trouvée dans le fichier HEIC')
    }

    // Prendre la première image
    const image = data[0]
    const width = image.get_width()
    const height = image.get_height()

    // Créer un canvas pour la conversion
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    const imageData = context.createImageData(width, height)

    // Décoder l'image dans le canvas
    await new Promise((resolve, reject) => {
      image.display(imageData, (displayData) => {
        if (!displayData) {
          return reject(new Error('Erreur lors du décodage HEIC'))
        }
        resolve()
      })
    })

    context.putImageData(imageData, 0, 0)

    // Convertir le canvas en JPEG data URL
    return canvas.toDataURL('image/jpeg', quality)
  } catch (error) {
    console.error('Erreur lors de la conversion HEIC:', error)
    throw new Error(`Impossible de convertir le fichier HEIC: ${error.message}`)
  }
}

/**
 * Convertit un fichier HEIC en Blob JPEG
 * @param {File} file - Le fichier HEIC à convertir
 * @param {number} quality - Qualité JPEG (0-1), par défaut 0.9
 * @returns {Promise<Blob>} Blob du JPEG converti
 */
export async function convertHeicToBlob (file, quality = 0.9) {
  try {
    const dataUrl = await convertHeicToJpeg(file, quality)
    
    // Convertir data URL en Blob
    const response = await fetch(dataUrl)
    return await response.blob()
  } catch (error) {
    console.error('Erreur lors de la conversion HEIC en Blob:', error)
    throw error
  }
}


